import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { dateToUTCNoon } from '../../utils/dateHelpers';
import DateTimePickerInput from '../../components/DateTimePickerInput';

interface Offer {
  id: string;
  name: string;
  offerType: string;
  slaCategory: string;
  environmentType: string | null;
  inactive: boolean;
}


interface FormData {
  name: string;
  type: string;
  offerId: string;
  startDate: string;
  endDate: string;
  price: string;
  priceEffectiveDate: string;
  inactive: boolean;
  isTaxable: boolean;
  planProvisioningDate: string;
}

const PlanForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    type: 'Service_Fee',
    offerId: '',
    startDate: '',
    endDate: '',
    price: '0.00',
    priceEffectiveDate: '',
    inactive: false,
    isTaxable: false,
    planProvisioningDate: ''
  });

  const [offers, setOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string>('');

  // Check if user has permission to create/update service plans
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const requiredAction = isEditing ? 'update' : 'create';
  const hasPermission = can('service_plans', requiredAction);

  const fetchOffersByType = async (type: string) => {
    try {
      const response = await api.get(`/offers/by-type/${type}`);
      setFilteredOffers(response.data);
      setOffers(response.data); // Keep full list for lookup
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Failed to load offers');
      setFilteredOffers([]);
    }
  };

  const fetchPlan = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await api.get(`/service-plans/${id}`);
      const plan = response.data;

      setFormData({
        name: plan.name || '',
        type: plan.type,
        offerId: plan.offerId,
        startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : '',
        endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
        price: plan.price.toString(),
        priceEffectiveDate: plan.priceEffectiveDate ? new Date(plan.priceEffectiveDate).toISOString().split('T')[0] : '',
        inactive: plan.inactive,
        isTaxable: plan.isTaxable,
        planProvisioningDate: plan.planProvisioningDate ? new Date(plan.planProvisioningDate).toISOString().split('T')[0] : ''
      });
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast.error('Failed to load service plan');
      navigate('/service-plans');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (!hasPermission) {
      navigate('/service-plans');
      return;
    }
    if (isEditing) {
      fetchPlan();
    }
  }, [hasPermission, permissionsLoading, navigate, isEditing, fetchPlan]);

  useEffect(() => {
    // Filter offers by selected type
    if (formData.type) {
      fetchOffersByType(formData.type);
    }
  }, [formData.type]);

  useEffect(() => {
    // Update SLA when offer is selected
    if (formData.offerId && offers.length > 0) {
    }
  }, [formData.offerId, offers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => {
        const newData = { ...prev, [name]: value };

        // Real-time date validation using the NEW values
        if (name === 'startDate' || name === 'endDate') {
          const startDate = name === 'startDate' ? value : prev.startDate;
          const endDate = name === 'endDate' ? value : prev.endDate;

          if (startDate && endDate) {
            if (new Date(endDate) <= new Date(startDate)) {
              setDateError('End Date must be after Start Date');
            } else {
              setDateError('');
            }
          } else {
            setDateError('');
          }
        }

        return newData;
      });

      // Reset offer selection when type changes
      if (name === 'type') {
        setFormData(prev => ({
          ...prev,
          offerId: '',
          price: value === 'Service_Fee' ? '0.00' : prev.price
        }));
      }
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const isPriceRequired = formData.type === 'Service_Flat';
    if (!formData.name || !formData.type || !formData.offerId || !formData.startDate || !formData.endDate || !formData.priceEffectiveDate || (isPriceRequired && !formData.price)) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      setDateError('End Date must be after Start Date');
      return;
    }

    if (isPriceRequired && parseFloat(formData.price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        ...formData,
        price: formData.type === 'Service_Fee' ? 0 : parseFloat(formData.price),
        startDate: dateToUTCNoon(formData.startDate),
        endDate: dateToUTCNoon(formData.endDate),
        priceEffectiveDate: dateToUTCNoon(formData.priceEffectiveDate),
        planProvisioningDate: formData.planProvisioningDate ? dateToUTCNoon(formData.planProvisioningDate) : null,
      };

      if (isEditing) {
        await api.put(`/service-plans/${id}`, payload);
        toast.success('Service plan updated successfully');
      } else {
        await api.post('/service-plans', payload);
        toast.success('Service plan created successfully');
      }

      navigate('/service-plans');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} service plan`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show permission denied if user lacks permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to {isEditing ? 'edit' : 'create'} service plans.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/service-plans')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            ← Back to Service Plans
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">{isEditing ? 'Edit Service Plan' : 'Create Service Plan'}</h1>
          </div>
          <form onSubmit={handleSubmit} className="p-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Plan Name - Full width with consistent alignment */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Name <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <div className="flex">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        placeholder="Enter a descriptive name for this service plan"
                        style={{width: 'calc(200% + 24px)'}}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                {/* Space for extended input */}
              </div>
            </div>

            {/* Two-column section */}
            <div className="grid grid-cols-12 gap-6 mb-6">

              {/* Type */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="Service_Fee">Service Fee</option>
                      <option value="Service_Flat">Service Flat</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Offer */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="offerId"
                      value={formData.offerId}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">Select an offer</option>
                      {filteredOffers.map((offer) => (
                        <option key={offer.id} value={offer.id}>
                          {offer.name} ({offer.offerType})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-12 gap-6 mb-6">

              {/* Start Date */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="startDate"
                      value={formData.startDate}
                      onChange={(val) => {
                        setFormData(prev => {
                          const newData = { ...prev, startDate: val };
                          if (val && prev.endDate) {
                            if (new Date(prev.endDate) <= new Date(val)) {
                              setDateError('End Date must be after Start Date');
                            } else {
                              setDateError('');
                            }
                          } else {
                            setDateError('');
                          }
                          return newData;
                        });
                      }}
                      showTimeSelect={false}
                      required
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* End Date */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      End Date <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="endDate"
                      value={formData.endDate}
                      onChange={(val) => {
                        setFormData(prev => {
                          const newData = { ...prev, endDate: val };
                          if (prev.startDate && val) {
                            if (new Date(val) <= new Date(prev.startDate)) {
                              setDateError('End Date must be after Start Date');
                            } else {
                              setDateError('');
                            }
                          } else {
                            setDateError('');
                          }
                          return newData;
                        });
                      }}
                      min={formData.startDate}
                      showTimeSelect={false}
                      required
                      error={!!dateError}
                      className="w-full"
                    />
                    {dateError && (
                      <p className="mt-1 text-sm text-red-600">{dateError}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Third row */}
            <div className="grid grid-cols-12 gap-6 mb-6">

              {/* Price Effective Date */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Price Effective Date <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="priceEffectiveDate"
                      value={formData.priceEffectiveDate}
                      onChange={(val) => setFormData(prev => ({ ...prev, priceEffectiveDate: val }))}
                      showTimeSelect={false}
                      required
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Plan Provisioning Date */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Plan Provisioning Date
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="planProvisioningDate"
                      value={formData.planProvisioningDate}
                      onChange={(val) => setFormData(prev => ({ ...prev, planProvisioningDate: val }))}
                      showTimeSelect={false}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Price field - Only show for Service Flat */}
            {formData.type === 'Service_Flat' && (
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price <span className="text-red-500 ml-1">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Checkboxes */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="inactive"
                    name="inactive"
                    checked={formData.inactive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="inactive" className="ml-2 block text-sm text-gray-900">
                    Inactive
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mark this plan as inactive</p>
              </div>
              <div className="col-span-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isTaxable"
                    name="isTaxable"
                    checked={formData.isTaxable}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isTaxable" className="ml-2 block text-sm text-gray-900">
                    Is Taxable
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Subject to tax calculations</p>
              </div>
            </div>
            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/service-plans')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 transition-colors"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  `${isEditing ? 'Update' : 'Create'} Plan`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlanForm;